import React, { type FC } from 'react';
import { checkIfPropertyIsOnMarket, formatFloat, getPricePerSqFt, getPropertyAddress } from '../../utility/utility_functions';
import { LoadingSpinner } from '../icons/OurIcons';
import { DECISION_MAKING_CUSTOM_FIELDS, getCustomDataItems } from '../../utility/customListFields';

type PropertySummaryProps = {
  propertyData: any;
  listSort?: any;
  loading?: boolean;
}

const lineItemsStyling = 'pt-2 pb-6 text-sm font-bold';
const smallTxtStyling = 'pt-1 text-xs whitespace-nowrap';

const PropertySummary: FC<PropertySummaryProps> = ({ propertyData, listSort = null, loading = false }) => {
  const getListUploadDataForThisList = () => {
    const listUploadId = listSort?.list_upload_id;
    if (!listUploadId) {
      return undefined;
    }
    const allCustomListUploadData = propertyData?.list_uploads_properties_data ?? [];
    return allCustomListUploadData.filter((listUploadData) => listUploadData.list_upload_id == listUploadId).at(0);
  };

  const getCustomListId = (): string => {
    return getListUploadDataForThisList()?.auction_number ?? 'N/A';
  };

  // Which of these make the Decision Making page is still to be decided; for now show everything filled in
  const customDataItems = getCustomDataItems(getListUploadDataForThisList(), DECISION_MAKING_CUSTOM_FIELDS);
  const showPropertySummaryContent = propertyData && propertyData.id;

  const propertySummaryContent = () => {
    if (loading) {
      return (
        <div id='property-summary-loading-container' role="status" className='flex items-center justify-center h-96'>
          <LoadingSpinner />
        </div>
      );
    }

    if (!showPropertySummaryContent) {
      return (
        <div id='property-summary-error-container' role="status" className='flex items-center justify-center h-96'>
          <p>No property data found</p>
        </div>
      );
    }

    return (
      <div className='px-3'>
        <p id='property-summary-address' className='text-xs mt-5 font-bold text-center'>{getPropertyAddress(propertyData)}</p>
        <div className='grid grid-cols-3 gap-x-6 pt-5 rounded-xl text-center m-auto justify-center'>
          <ul>
            <hr />
            <h6 className={smallTxtStyling}>Currently on Market</h6>
            <li className={lineItemsStyling}>{checkIfPropertyIsOnMarket(propertyData?.status)}</li>
            <hr />
            <h6 className={smallTxtStyling}>Year Build</h6>
            <li className={lineItemsStyling}>{propertyData?.year_built ?? 'N/A'}</li>
            <hr />
            <h6 className={smallTxtStyling}>Neighborhood</h6>
            <li className={lineItemsStyling}>{propertyData?.subdivision ?? 'N/A'}</li>
          </ul>
          <ul>
            <hr />
            <h6 className={smallTxtStyling}>Sq Ft</h6>
            <li className={`${lineItemsStyling} ${!propertyData?.sqft ? 'text-red-500' : ''}`}>{propertyData?.sqft ? formatFloat(propertyData?.sqft, 0) : 'N/A'}</li>
            <hr />
            <h6 className={smallTxtStyling}>Beds</h6>
            <li className={lineItemsStyling}>{propertyData?.bedrooms ?? 'N/A'}</li>
            <hr />
            <h6 className={smallTxtStyling}>Baths</h6>
            <li className={lineItemsStyling}>{propertyData?.bathrooms ?? 'N/A'}</li>
          </ul>
          <ul>
            <hr />
            <h6 className={smallTxtStyling}>Stories</h6>
            <li className={lineItemsStyling}>{propertyData?.stories ?? 'N/A'}</li>
            <hr />
            <h6 className={smallTxtStyling}>Garage</h6>
            <li className={lineItemsStyling}>{propertyData?.garage_spaces ?? 'N/A'}</li>
            <hr />
            <h6 className={smallTxtStyling}>Acres</h6>
            <li className={lineItemsStyling}>{propertyData?.lot_size_acres ?? 'N/A'}</li>
          </ul>
        </div>
        <ul className='grid grid-cols-3 gap-x-6 text-center'>
          <div>
            <hr/>
            <h6 className={smallTxtStyling}>Custom List ID</h6>
            <li className={lineItemsStyling}>{getCustomListId()}</li>
          </div>
          {checkIfPropertyIsOnMarket(propertyData?.status) === 'Yes' &&
          <div>
            <hr />
            <h6 className={smallTxtStyling}>List Price</h6>
            <li className={lineItemsStyling}>{propertyData?.list_price ? `$${formatFloat(propertyData?.list_price, 0)}` : '-'}
            </li>
          </div>
          }
          {checkIfPropertyIsOnMarket(propertyData?.status) === 'Yes' &&
          <div>
            <hr />
            <h6 className={smallTxtStyling}>List Price $/Sq Ft</h6>
            <li className={lineItemsStyling}>{getPricePerSqFt(propertyData) ? `$${formatFloat(getPricePerSqFt(propertyData), 2)}` : '-'}</li>
          </div>
          }
        </ul>
        {customDataItems.length > 0 &&
          <div id='property-summary-custom-data' className='mt-2'>
            <hr />
            <h6 className={`${smallTxtStyling} text-center`}>Custom Data</h6>
            <ul className='grid grid-cols-3 gap-x-6 text-center'>
              {customDataItems.map((item) => (
                <li key={item.name} className='pt-2 pb-3'>
                  <p className='text-xs text-gray-500'>{item.name}</p>
                  <p className='text-sm font-bold'>{item.value}</p>
                </li>
              ))}
            </ul>
          </div>
        }
      </div>
    );
  };

  return (
    <div id='property-summary-container' className="w-contain content-center justify-content-center ">
      <div className="flex justify-between m-3 items-center">
        <h3 className="whitespace-nowrap text-extrabold text-lg text-indigo-500">Property Summary</h3>
        {showPropertySummaryContent &&
            <a
              href={`/properties/${propertyData?.id}`}
              target='_blank'
              className="rounded whitespace-nowrap bg-indigo-600 px-2 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              View Property Details
            </a>
        }
      </div>
      <hr className='mt-3' />
      <div className='h-[28rem] overflow-y-auto'>
        {propertySummaryContent()}
      </div>

    </div>
  );
};

export default PropertySummary;
